/**
 * LLM (Large Language Model) Types
 * ============================================
 * Shared types for LLM integration across the RuralOps platform.
 *
 * Which model serves which task type is **not** expressed here — it lives in
 * the `ai_models` / `ai_model_bindings` catalog, resolved once per task and
 * stamped on `llm_tasks`. What each call site *requires* of a model lives in
 * `./AIModel.ts`.
 */
// ============================================
// ENUMS
// ============================================
/**
 * Every provider the platform can bill against. Values stay lowercase because
 * they are the `llm_tasks.provider` snapshot and every historical
 * `platform_billing_usage_events.provider` row.
 */
export var LLMProvider;
(function (LLMProvider) {
    LLMProvider["OPENAI"] = "openai";
    LLMProvider["ANTHROPIC"] = "anthropic";
    LLMProvider["GROQ"] = "groq";
    LLMProvider["ELEVENLABS"] = "elevenlabs";
    LLMProvider["DEEPGRAM"] = "deepgram";
})(LLMProvider || (LLMProvider = {}));
// ============================================
// LLM TASK TYPES
// ============================================
/**
 * Types of LLM tasks supported by the platform. Every value here is live: each
 * one has a row in `LLM_TASK_PROFILES` and a binding in the catalog.
 */
export var LLMTaskType;
(function (LLMTaskType) {
    LLMTaskType["CHAT"] = "CHAT";
    LLMTaskType["MEMORY_EXTRACTION"] = "MEMORY_EXTRACTION";
    /** The model-written notes of the conversation summary (assistant history). */
    LLMTaskType["CONVERSATION_SUMMARY"] = "CONVERSATION_SUMMARY";
    LLMTaskType["MEDIA_ANALYSIS"] = "MEDIA_ANALYSIS";
    /**
     * On-demand text-to-speech for planning tasks (Task Voice). Char-priced:
     * the ledger stores `charCount` in the `inputTokens` column against a
     * USD-per-million-CHARS pricing row. Excluded from token-count displays
     * (see `llmTaskService.getUsageSummary`) since its unit is chars, not tokens.
     */
    LLMTaskType["VOICE_SYNTHESIS"] = "VOICE_SYNTHESIS";
    /**
     * Speech-to-text transcription for assistant chat voice input.
     * Seconds-priced: the ledger stores `ceil(audio seconds)` in the
     * `inputTokens` column against a USD-per-million-SECONDS pricing row.
     * Excluded from token-count displays (see `llmTaskService.getUsageSummary`)
     * since its unit is seconds, not tokens.
     */
    LLMTaskType["VOICE_TRANSCRIPTION"] = "VOICE_TRANSCRIPTION";
})(LLMTaskType || (LLMTaskType = {}));
/**
 * Status of an LLM task
 */
export var LLMTaskStatus;
(function (LLMTaskStatus) {
    LLMTaskStatus["PROCESSING"] = "PROCESSING";
    LLMTaskStatus["COMPLETED"] = "COMPLETED";
    LLMTaskStatus["FAILED"] = "FAILED";
})(LLMTaskStatus || (LLMTaskStatus = {}));
/**
 * Entity types that can be associated with LLM tasks
 */
export var LLMEntityType;
(function (LLMEntityType) {
    LLMEntityType["CONVERSATION"] = "Conversation";
})(LLMEntityType || (LLMEntityType = {}));
//# sourceMappingURL=LLM.js.map